import React from 'react'
import {
  Box,
  Button,
  Col,
  Container,
  DialogContent,
  DialogTitle,
  Row,
  TextField,
  Typography,
  Radio,
  FormControlLabel,
  Paper,
  Switch,
  Divider,
  DialogActionsWithStepper,
} from '@applift/factor'

import { Action, PixelInfo } from '../../../models/Pixel'
import { ConversionDurationSettings } from './ConversionDurationSetting'
import { RepeatedConversionSetting } from './RepeatedConversionSetting'
import { ConversionModelingSetting } from './ConversionModelingSetting'
import {
  usePixelAdvancedSettingList,
  usePixelConversionAttributionList,
} from '../../../hooks/usePixel'
import {
  ADVANCED_SETTING_NAME,
  ATTRIBUTION_IDS,
  DEFAULT_VALUES,
  MAX_NAME_CHARS,
} from '../../../constants/constants'

export interface PixelDetailsProps {
  onClose: () => void
  setStep: React.Dispatch<React.SetStateAction<number>>
  type: 'create' | 'edit'
  dispatch: React.Dispatch<Action>
  data: PixelInfo
  advancedSettingEnabled: boolean
  toggleAdvancedSettingVisibility: (isVisible: boolean) => void
}

const getTypography = (text: string) => (
  <Typography sx={{ textColor: 'neutral-500', mb: 16 }}>{text}</Typography>
)

export const PixelDetails = (props: PixelDetailsProps) => {
  const {
    onClose,
    setStep,
    type,
    dispatch,
    data,
    toggleAdvancedSettingVisibility,
    advancedSettingEnabled,
  } = props

  const [nameHasError, setNameError] = React.useState(false)

  const { name, attributionId, conversionSetting } = data

  const isCreateFlow = type === 'create'

  const attributionTypes = usePixelConversionAttributionList()?.map(item => ({
    ...item,
    id: item.id.toString(),
  }))

  const advancedDurationSettings = usePixelAdvancedSettingList()

  const postClickDefaultSetting = advancedDurationSettings?.data?.data.find(
    item => item.name === ADVANCED_SETTING_NAME.POST_CLICK
  )
  const postViewDefaultSetting = advancedDurationSettings?.data?.data.find(
    item => item.name === ADVANCED_SETTING_NAME.POST_VIEW
  )

  const getRepeatedConversion = () => {
    const frequency = conversionSetting.repeatConversion.frequency
    const unit = conversionSetting.repeatConversion.unit.toLowerCase()
    if (
      conversionSetting.repeatConversion.count !==
        DEFAULT_VALUES.CONVERSION_COUNT &&
      conversionSetting.repeatConversion.frequency !==
        DEFAULT_VALUES.CONVERSION_FREQUENCY
    ) {
      return getTypography(
        `Count 1 conversion per user every ${frequency} ${unit}${
          frequency > 1 ? 's' : ''
        }`
      )
    }

    return getTypography(
      conversionSetting.repeatConversion.count ===
        DEFAULT_VALUES.CONVERSION_COUNT
        ? 'Count all conversions per user'
        : 'Count 1 conversion per user'
    )
  }

  const getAdditionalSetting = () => {
    let duration = (
      <Typography sx={{ textColor: 'neutral-500', mb: 16, display: 'flex' }}>
        <Typography sx={{ mr: 16 }}>
          {postViewDefaultSetting &&
            `${postViewDefaultSetting.displayName}: ${conversionSetting.conversionDuration.view} Days`}
        </Typography>
        <>
          {postClickDefaultSetting &&
            `${postClickDefaultSetting.displayName}: ${conversionSetting.conversionDuration.click} Days`}
        </>
      </Typography>
    )

    if (
      attributionId === ATTRIBUTION_IDS.VIEW_BASED ||
      attributionId === ATTRIBUTION_IDS.CLICK_BASED
    ) {
      duration = getTypography(
        attributionId === ATTRIBUTION_IDS.VIEW_BASED
          ? `${postViewDefaultSetting?.displayName}: ${
              conversionSetting.conversionDuration.view
            } Day${conversionSetting.conversionDuration.view > 1 ? 's' : ''}`
          : `${postClickDefaultSetting?.displayName}: ${conversionSetting.conversionDuration.click} Days`
      )
    }

    return [
      { key: 'Conversion Duration Settings', value: duration },
      {
        key: 'Repeat Conversions',
        value: getRepeatedConversion(),
      },
      {
        key: 'Conversion Modeling',
        value: getTypography(
          `Cross Device - Cross IP Modeling: ${
            conversionSetting.crossModelling ? 'Enabled' : 'Disabled'
          }`
        ),
      },
    ]
  }

  return (
    <>
      <DialogTitle onClose={onClose}>Pixel Details</DialogTitle>
      <DialogContent dividers>
        <Container
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            bgColor: 'neutral-0',
          }}
        >
          <Row
            sx={{
              width: 100,
              mb: 24,
            }}
            xs={2}
          >
            <Col>
              <TextField
                value={name}
                onChange={e => {
                  setNameError(e.target.value.trim().length > MAX_NAME_CHARS)
                  dispatch({
                    type: 'UPDATE_NAME',
                    payload: e.target.value.trim() ? e.target.value : '',
                  })
                }}
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'between',
                    }}
                  >
                    <Typography variant="label">Pixel Name</Typography>
                    <Typography variant="label">
                      {`${name.length}/${MAX_NAME_CHARS}`}
                    </Typography>
                  </Box>
                }
                placeholder="Enter pixel name"
                variant="outlinedDash"
                fullWidth
                helperText={
                  nameHasError ? (
                    <Typography variant="label" color="error">
                      Character limit exceeded
                    </Typography>
                  ) : null
                }
                error={nameHasError}
              />
            </Col>
            <Col
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="label"
                sx={{
                  textColor: 'neutral-600',
                }}
                gutterBottom={false}
              >
                Conversion Attribution
              </Typography>
              <Box sx={{ display: 'flex' }}>
                {attributionTypes?.map(attribution => (
                  <FormControlLabel
                    key={attribution.id}
                    onChange={() =>
                      dispatch({
                        type: 'UPDATE_ATTRIBUTION_ID',
                        payload: attribution.id,
                      })
                    }
                    checked={attributionId === attribution.id}
                    control={
                      <Radio
                        sx={{
                          textColor: !isCreateFlow
                            ? 'primary-200'
                            : 'primary-500',
                        }}
                      />
                    }
                    disabled={!isCreateFlow}
                    label={attribution.displayName}
                    labelPlacement="end"
                    sx={{ mr: 16 }}
                  />
                ))}
              </Box>
              <Typography
                variant="label"
                sx={{
                  textColor: 'neutral-600',
                }}
              >
                {
                  attributionTypes?.find(
                    attribution => attribution.id === attributionId
                  )?.description
                }
              </Typography>
            </Col>
          </Row>

          <Paper elevation={2} sx={{ width: 100, padding: 24 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'between',
                mb: 24,
              }}
            >
              <Typography
                component="h5"
                variant="bodySmall"
                weight="demi"
                gutterBottom={false}
              >
                Advanced Settings
              </Typography>
              {isCreateFlow && (
                <FormControlLabel
                  sx={{ alignSelf: 'end' }}
                  checked={advancedSettingEnabled}
                  onChange={event =>
                    //@ts-ignore
                    toggleAdvancedSettingVisibility(event.target.checked)
                  }
                  control={<Switch size="medium" />}
                  label=""
                />
              )}
            </Box>
            {!advancedSettingEnabled ? (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {getAdditionalSetting().map(setting => (
                  <Box
                    key={setting.key}
                    sx={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <Typography sx={{ mb: 2 }}>{setting.key}</Typography>
                    {setting.value}
                  </Box>
                ))}
              </Box>
            ) : (
              <>
                <ConversionDurationSettings dispatch={dispatch} data={data} />

                <Divider orientation="horizontal" sx={{ my: 16 }} />

                <RepeatedConversionSetting dispatch={dispatch} data={data} />

                <Divider orientation="horizontal" sx={{ my: 16 }} />

                <ConversionModelingSetting dispatch={dispatch} data={data} />
              </>
            )}
          </Paper>
        </Container>
      </DialogContent>

      <DialogActionsWithStepper maxSteps={3} activeStep={0}>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={!name.length || name.trim().length > MAX_NAME_CHARS}
          onClick={() => setStep(2)}
        >
          Next
        </Button>
      </DialogActionsWithStepper>
    </>
  )
}
